import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Check, X, AlertCircle } from 'lucide-react';

const SearchableDropdown = ({
  options = [],
  value = null,
  onChange = () => { },
  onSearch = () => { },
  placeholder = "Search and select...",
  loading = false,
  error = null,
  disabled = false,
  className = "",
  searchPlaceholder = "Type to search...",
  noResultsText = "No results found",
  minSearchLength = 2,
  debounceMs = 300,
  onBlur = () => { },
  matchMode = "contains"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Get selected option
  const selectedOption = options.find(option => option.id === value);

  // Keep onSearch stable using a ref to avoid effect dependency issues
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Filter options based on search term
  useEffect(() => {
    // When using server-side search, don't do client-side filtering if onSearch is actually doing something
    const hasSearchHandler = onSearch && typeof onSearch === 'function' && onSearch.toString() !== '() => {}' && onSearch.toString() !== '()=>{}';

    if (hasSearchHandler) {
      setFilteredOptions(options);
    } else {
      // Client-side filtering for static options
      if (!searchTerm) {
        setFilteredOptions(options);
      } else {
        const lowerSearch = searchTerm.toLowerCase().trim();
        const matchesOption = (option) => {
          if (matchMode !== 'wordStart') {
            return (
              option.searchText?.toLowerCase().includes(lowerSearch) ||
              option.displayText?.toLowerCase().includes(lowerSearch) ||
              option.title?.toLowerCase().includes(lowerSearch)
            );
          }

          const searchable = `${option.searchText || ''} ${option.displayText || ''} ${option.title || ''}`.toLowerCase();
          return searchable
            .split(/[^a-z0-9@._+-]+/i)
            .filter(Boolean)
            .some(token => token.startsWith(lowerSearch));
        };
        const filtered = options.filter(option =>
          matchesOption(option)
        );
        setFilteredOptions(filtered);
      }
    }
  }, [searchTerm, options, onSearch, matchMode]);

  // Handle search with debouncing
  useEffect(() => {
    // Don't trigger search if dropdown is closed
    if (!isOpen) return;
    
    // Don't trigger searching state if there's no actual search to perform or if term is empty
    const hasSearchHandler = onSearch && typeof onSearch === 'function' && onSearch.toString() !== '() => {}' && onSearch.toString() !== '()=>{}';

    if (searchTerm.length >= minSearchLength && hasSearchHandler) {
      setIsSearching(true);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        if (onSearchRef.current) {
          onSearchRef.current(searchTerm);
        }
        setIsSearching(false);
      }, debounceMs);
    } else {
      setIsSearching(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, minSearchLength, debounceMs, onSearch, isOpen]);


  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Only trigger close and blur if it was actually open
        if (isOpen) {
          setIsOpen(false);
          // Don't reset searchTerm when using server-side search to preserve filtered results
          if (!onSearch) {
            setSearchTerm('');
          }
          setHighlightedIndex(-1);
          onBlur();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearch, isOpen, onBlur]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        searchInputRef.current?.focus();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        // Don't reset searchTerm when using server-side search
        if (!onSearch) {
          setSearchTerm('');
        }
        setHighlightedIndex(-1);
        break;
      default:
        // No default action needed for other keys
        break;
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    onChange(option.id);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    // Don't reset searchTerm when using server-side search
    if (!onSearch) {
      setSearchTerm('');
    }
    // Removed onBlur() here to avoid premature validation errors
  };

  // Handle dropdown toggle
  const handleToggle = () => {
    if (disabled) return;
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen) {
      // For server-side search, trigger search only if no value is selected
      // If a value is already selected, don't reset the search on reopen
      if (onSearch && typeof onSearch === 'function') {
        if (!value) {
          // No value selected, do initial empty search
          setSearchTerm('');
          onSearch('');
        } else {
          // Value already selected, just clear search term for new search
          setSearchTerm('');
        }
      }
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      // If closing via toggle, we can trigger blur as it's an explicit "close" action
      onBlur();
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent cursor-pointer transition-colors
          ${disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-900 hover:border-gray-400'
          }
          ${error ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'}
          ${isOpen ? 'ring-2 ring-brand border-transparent' : ''}
        `}
      >
        <div className="flex items-center justify-between min-w-0">
          <span className={`${selectedOption ? 'text-gray-900' : 'text-gray-500'} truncate flex-1 min-w-0`}>
            {selectedOption ? selectedOption.displayText || selectedOption.title : placeholder}
          </span>
          <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
            {selectedOption && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {loading || isSearching ? (
              <div className="px-3 py-2 text-sm text-gray-500 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand mr-2"></div>
                {isSearching ? 'Searching...' : 'Loading...'}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm.length >= minSearchLength ? noResultsText : 'Start typing to search...'}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors border-b border-gray-50 last:border-0
                    ${index === highlightedIndex ? 'bg-gray-50' : ''}
                    ${option.id === value ? 'bg-brand/10 text-brand font-medium' : 'text-gray-900'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {option.renderOption ? (
                        option.renderOption(option)
                      ) : (
                        <>
                          <div className="font-medium truncate">
                            {option.displayText || option.title}
                          </div>
                          {option.courseCode && option.displayText && (
                            <div className="text-xs text-gray-500 mt-1">
                              {option.courseCode} • {option.category} • {option.level}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {option.id === value && (
                      <Check className="h-4 w-4 text-brand flex-shrink-0 ml-2" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with search info */}
          {searchTerm.length > 0 && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
              {onSearch && typeof onSearch === 'function' ? (
                // For server-side search, show current options count
                searchTerm.length >= minSearchLength ? (
                  `${filteredOptions.length} result${filteredOptions.length !== 1 ? 's' : ''} found`
                ) : (
                  `Type ${minSearchLength - searchTerm.length} more character${minSearchLength - searchTerm.length !== 1 ? 's' : ''} to search`
                )
              ) : (
                // For client-side search, show filtered count
                <>
                  {filteredOptions.length} result{filteredOptions.length !== 1 ? 's' : ''} found
                  {searchTerm.length < minSearchLength && (
                    <span className="ml-2">• Type {minSearchLength - searchTerm.length} more character{minSearchLength - searchTerm.length !== 1 ? 's' : ''}</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-600 font-medium flex items-center">
          {/* <AlertCircle className="h-4 w-4 mr-1" /> */}
          {error}
        </p>
      )}
    </div>
  );
};

export default SearchableDropdown;
